import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  // ADD THIS: Debounce duplicate check to prevent interference
  #debouncedDuplicateCheck = debounce(this.#removeDuplicateItems, 500).bind(this);
  #duplicateRemovalInProgress = false;
  // ADD THIS: Track user-initiated actions
  #userInitiatedAction = false;
  #restrictedKeywords = ['sculpt', 'cardio', 'power', 'pack', 'mat'];

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.cartAdd, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    // Only check for duplicates if not from user action
    if (!this.#userInitiatedAction) {
      setTimeout(() => {
        this.#debouncedDuplicateCheck();
      }, 1000);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.cartAdd, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Checks if a product title contains restricted keywords.
   * @param {string} title - The product title.
   * @returns {boolean} True if the product is restricted.
   */
  #isRestrictedProduct(title) {
    if (!title) return false;
    const titleLower = title.toLowerCase().trim();
    return this.#restrictedKeywords.some(keyword => titleLower.includes(keyword.toLowerCase()));
  }

  /**
   * Removes duplicate items based on product title.
   * CHANGED: Only runs when NOT a user-initiated action.
   */
  #removeDuplicateItems() {
    // CRITICAL: Don't run if user is performing an action
    if (this.#duplicateRemovalInProgress || this.#userInitiatedAction) {
      return;
    }
    
    const cartItemRows = this.refs.cartItemRows || [];
    if (cartItemRows.length === 0) return;

    const titleMap = new Map();
    const duplicatesToRemove = [];
    const restrictedProducts = [];

    cartItemRows.forEach((row, index) => {
      const titleElement = row.querySelector('.cart-items__title');
      if (!titleElement) return;

      const productTitle = titleElement.textContent.trim();
      if (!productTitle) return;

      const lineNumber = index + 1;
      const isRestricted = this.#isRestrictedProduct(productTitle);

      if (!titleMap.has(productTitle)) {
        titleMap.set(productTitle, { line: lineNumber, isRestricted });
        if (isRestricted) {
          restrictedProducts.push({ line: lineNumber, title: productTitle });
        }
      } else {
        duplicatesToRemove.push({
          line: lineNumber,
          title: productTitle,
          isRestricted,
        });
      }
    });

    // Sort duplicates: remove restricted product duplicates first
    duplicatesToRemove.sort((a, b) => {
      if (a.isRestricted && !b.isRestricted) return -1;
      if (!a.isRestricted && b.isRestricted) return 1;
      return 0;
    });

    if (duplicatesToRemove.length > 0) {
      this.#duplicateRemovalInProgress = true;
      
      console.log(`Found ${duplicatesToRemove.length} duplicate item(s) to remove`);
      
      duplicatesToRemove.reverse().forEach(({ line, title, isRestricted }, index) => {
        console.log(`Removing duplicate ${isRestricted ? 'restricted ' : ''}product: ${title} at line ${line}`);
        // Small delay between removals
        setTimeout(() => {
          this.updateQuantity({
            line,
            quantity: 0,
            action: 'clear',
          });
        }, index * 100);
      });

      setTimeout(() => {
        this.#duplicateRemovalInProgress = false;
        // Only re-check if not user action
        if (!this.#userInitiatedAction) {
          setTimeout(() => {
            this.#debouncedDuplicateCheck();
          }, 1000);
        }
      }, duplicatesToRemove.length * 200 + 500);
    }

    // Ensure restricted products have quantity of 1 (only if not user action)
    if (!this.#userInitiatedAction && restrictedProducts.length > 0) {
      restrictedProducts.forEach(({ line, title }) => {
        const quantitySelector = this.refs.quantitySelectors?.[line - 1];
        const quantityInput = quantitySelector?.querySelector('input[type="number"]');
        
        if (quantityInput && parseInt(quantityInput.value) > 1) {
          console.log(`Restricted product "${title}" at line ${line} has quantity > 1, resetting to 1`);
          quantityInput.value = '1';
          quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
          quantityInput.dispatchEvent(new Event('blur', { bubbles: true }));
        }
      });
    }
  }

  /**
   * Handles CartAddEvent from third-party widgets.
   */
  #handleCartAdd = (event) => {
    // Mark as not user-initiated so duplicate check can run
    this.#userInitiatedAction = false;
    
    setTimeout(() => {
      if (!this.#userInitiatedAction) {
        this.#debouncedDuplicateCheck();
      }
    }, 800);
    
    setTimeout(() => {
      if (!this.#userInitiatedAction) {
        this.#debouncedDuplicateCheck();
      }
    }, 2000);
  };

  /**
   * CHANGED: Mark as user-initiated when quantity changes.
   */
  #onQuantityChange(event) {
    const quantity = event.detail.quantity;
    const line = event.detail.cartLine;

    if (!line) return;

    // CRITICAL: Mark as user-initiated action
    this.#userInitiatedAction = true;

    // Check if this is a restricted product
    const cartItemRow = this.refs.cartItemRows?.[line - 1];
    if (cartItemRow) {
      const titleElement = cartItemRow.querySelector('.cart-items__title');
      if (titleElement) {
        const productTitle = titleElement.textContent.trim();
        if (this.#isRestrictedProduct(productTitle) && quantity > 1) {
          console.log(`Attempted to set quantity > 1 for restricted product "${productTitle}", forcing to 1`);
          const quantityInput = this.refs.quantitySelectors?.[line - 1]?.querySelector('input[type="number"]');
          if (quantityInput) {
            quantityInput.value = '1';
            quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
            quantityInput.dispatchEvent(new Event('blur', { bubbles: true }));
          }
          setTimeout(() => {
            this.#userInitiatedAction = false;
          }, 2000);
          return;
        }
      }
    }

    if (quantity === 0) {
      // CRITICAL: User is removing - allow it and prevent duplicate check interference
      this.onLineItemRemove(line);
      setTimeout(() => {
        this.#userInitiatedAction = false;
      }, 3000);
      return;
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();

    setTimeout(() => {
      this.#userInitiatedAction = false;
    }, 2000);
  }

  /**
   * CHANGED: Mark as user-initiated when removing items manually.
   */
  onLineItemRemove(line) {
    // CRITICAL: Mark as user-initiated to prevent duplicate removal interference
    this.#userInitiatedAction = true;
    
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) {
      setTimeout(() => {
        this.#userInitiatedAction = false;
      }, 1000);
      return;
    }

    const remove = () => {
      cartItemRowToRemove.remove();
      // Reset flag after removal completes
      setTimeout(() => {
        this.#userInitiatedAction = false;
      }, 2000);
    };

    if (prefersReducedMotion()) {
      remove();
      return;
    }

    cartItemRowToRemove.style.setProperty('--row-height', `${cartItemRowToRemove.clientHeight}px`);
    cartItemRowToRemove.classList.add('removing');

    onAnimationEnd(cartItemRowToRemove, remove);
  }

  /**
   * CHANGED: Only check duplicates if not user-initiated removal.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        // CRITICAL: Only check duplicates if NOT a user-initiated removal
        if (!this.#userInitiatedAction || config.quantity !== 0) {
          setTimeout(() => {
            if (!this.#userInitiatedAction) {
              this.#debouncedDuplicateCheck();
            }
          }, 500);
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
    
    // Reset flag on error
    setTimeout(() => {
      this.#userInitiatedAction = false;
    }, 1000);
  };

  /**
   * CHANGED: Only check duplicates if not user-initiated action.
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      
      // Only check if not user action
      if (!this.#userInitiatedAction) {
        setTimeout(() => {
          this.#debouncedDuplicateCheck();
        }, 500);
      }
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      
      if (!this.#userInitiatedAction) {
        setTimeout(() => {
          this.#debouncedDuplicateCheck();
        }, 1000);
      }
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}